import React, { useEffect, useRef, useState } from 'react';
import {
  FaPaperPlane,
  FaPaperclip,
  FaTimes,
  FaPhoneAlt,
  FaEnvelope,
  FaMapMarkerAlt,
  FaCheckCircle,
  FaExclamationTriangle,
} from 'react-icons/fa';
import SEO from '../components/SEO';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import {
  ACCEPT_ATTR,
  ALLOWED_EXTENSIONS,
  MAX_FILE_SIZE_MB,
  isAllowedFile,
  submitContactForm,
} from '../utils/contactSubmission';
import './Contact.css';

const initialForm = {
  name: '',
  phone: '',
  email: '',
  company: '',
  message: '',
};

const Contact = () => {
  const [form, setForm] = useState(initialForm);
  const [files, setFiles] = useState([]);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [status, setStatus] = useState(null); // { ok: bool, msg: string }
  const fileInputRef = useRef(null);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: undefined }));
    }
  };

  const handleFilesSelected = (e) => {
    const selected = Array.from(e.target.files || []);
    addFiles(selected);
    e.target.value = ''; // allow re-selecting the same file
  };

  const addFiles = (selected) => {
    const accepted = [];
    const rejected = [];
    selected.forEach((f) => {
      if (isAllowedFile(f)) accepted.push(f);
      else rejected.push(f);
    });
    setFiles((prev) => [...prev, ...accepted]);
    if (rejected.length) {
      setErrors((prev) => ({
        ...prev,
        files: `Format ou taille non accepté(s) : ${rejected
          .map((f) => f.name)
          .join(', ')}`,
      }));
    } else {
      setErrors((prev) => ({ ...prev, files: undefined }));
    }
  };

  const removeFile = (idx) => {
    setFiles((prev) => prev.filter((_, i) => i !== idx));
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const dropped = Array.from(e.dataTransfer.files || []);
    addFiles(dropped);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const validate = () => {
    const next = {};
    if (!form.name.trim()) next.name = 'Veuillez renseigner votre nom.';
    if (!form.email.trim()) next.email = 'Veuillez renseigner votre email.';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email))
      next.email = 'Adresse email invalide.';
    if (!form.phone.trim()) next.phone = 'Veuillez renseigner votre téléphone.';
    if (!form.message.trim() || form.message.trim().length < 10)
      next.message = 'Décrivez votre demande en quelques lignes (10 caractères minimum).';
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus(null);
    if (!validate()) return;
    setSubmitting(true);
    try {
      const result = await submitContactForm(form, files);
      if (result.ok) {
        setStatus({
          ok: true,
          msg:
            'Votre demande a bien été enregistrée. Nous vous répondrons dans les plus brefs délais.',
        });
        setForm(initialForm);
        setFiles([]);
      } else {
        setStatus({
          ok: false,
          msg: result.error || 'Une erreur est survenue. Veuillez réessayer.',
        });
      }
    } catch (err) {
      setStatus({
        ok: false,
        msg: 'Une erreur est survenue. Veuillez réessayer.',
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <SEO
        title="Contact ATAA REALTY — Cabinet d'Expertise Immobilière | Casablanca"
        description="Contactez ATAA REALTY pour vos missions d'expertise immobilière, conseil, études ou formation au Maroc. Hay Hassani, Casablanca — Tél : +212 6 66 94 80 69 — contact@ataarealty.com"
        canonical="https://ataarealty.com/contact"
        keywords="contact ATAA Realty, expert immobilier Casablanca contact, expertise immobilière Maroc devis, demande expertise immobilière"
      />
      <Navbar />

      {/* HERO */}
      <header className="contact-header">
        <div className="contact-overlay"></div>
        <div className="container contact-title-box">
          <span className="contact-eyebrow">Échangeons</span>
          <h1>Formulez votre demande</h1>
          <p>
            Décrivez votre projet, joignez vos documents et nous vous répondrons
            dans les meilleurs délais.
          </p>
        </div>
      </header>

      <section className="contact-section">
        <div className="container contact-grid">
          {/* INFO COLUMN */}
          <aside className="contact-info">
            <h3>Coordonnées</h3>
            <p className="contact-info-lead">
              Notre équipe est à votre disposition pour toute demande
              d'expertise, de conseil, d'études ou de formation.
            </p>

            <ul className="contact-info-list">
              <li>
                <FaMapMarkerAlt />
                <div>
                  <strong>Adresse</strong>
                  <span>
                    NR 75 Avenue Taib Mohammed Naciri,<br />
                    Lot Safaa, ETG 01 NR07,
                    Hay Hassani, Casablanca (MA)
                  </span>
                </div>
              </li>
              <li>
                <FaPhoneAlt />
                <div>
                  <strong>Téléphone</strong>
                  <a href="tel:+212522095653">+212 5 22 09 56 53 </a>
                  <a href="tel:+212666948069">+212 6 66 94 80 69 </a>
                  
                </div>
              </li>
              <li>
                <FaEnvelope />
                <div>
                  <strong>Email</strong>
                  <a href="mailto:contact@ataarealty.com">contact@ataarealty.com</a>
                  <a href="mailto:salaheddine.elkacimi@ataarealty.com">salaheddine.elkacimi@ataarealty.com</a>
                </div>
              </li>
            </ul>

            <div className="contact-info-note">
              <strong>Confidentialité</strong>
              <p>
                Les informations transmises sont traitées avec confidentialité,
                conformément à nos engagements professionnels.
              </p>
            </div>
          </aside>

          {/* FORM COLUMN */}
          <form className="contact-form" onSubmit={handleSubmit} noValidate>
            <h3>Votre demande</h3>

            <div className="form-row">
              <div className={`form-group ${errors.name ? 'has-error' : ''}`}>
                <label htmlFor="name">Nom complet *</label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={form.name}
                  onChange={handleChange}
                  placeholder="Votre nom"
                  autoComplete="name"
                />
                {errors.name && <span className="form-err">{errors.name}</span>}
              </div>

              <div className={`form-group ${errors.company ? 'has-error' : ''}`}>
                <label htmlFor="company">Entreprise / Organisation</label>
                <input
                  type="text"
                  id="company"
                  name="company"
                  value={form.company}
                  onChange={handleChange}
                  placeholder="(Optionnel)"
                  autoComplete="organization"
                />
              </div>
            </div>

            <div className="form-row">
              <div className={`form-group ${errors.email ? 'has-error' : ''}`}>
                <label htmlFor="email">Email *</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={form.email}
                  onChange={handleChange}
                  placeholder="vous@exemple.com"
                  autoComplete="email"
                />
                {errors.email && <span className="form-err">{errors.email}</span>}
              </div>

              <div className={`form-group ${errors.phone ? 'has-error' : ''}`}>
                <label htmlFor="phone">Téléphone *</label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={form.phone}
                  onChange={handleChange}
                  placeholder="+212 ..."
                  autoComplete="tel"
                />
                {errors.phone && <span className="form-err">{errors.phone}</span>}
              </div>
            </div>

            <div className={`form-group ${errors.message ? 'has-error' : ''}`}>
              <label htmlFor="message">Décrivez votre demande *</label>
              <textarea
                id="message"
                name="message"
                rows="6"
                value={form.message}
                onChange={handleChange}
                placeholder="Contexte, objectif, type de bien, calendrier indicatif..."
              />
              {errors.message && (
                <span className="form-err">{errors.message}</span>
              )}
            </div>

            {/* FILE UPLOAD */}
            <div className="form-group">
              <label>Documents joints</label>
              <div
                className="file-dropzone"
                onDrop={handleDrop}
                onDragOver={handleDragOver}
                onClick={() => fileInputRef.current?.click()}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    fileInputRef.current?.click();
                  }
                }}
              >
                <FaPaperclip />
                <p>
                  <strong>Cliquez pour sélectionner</strong> ou glissez vos
                  fichiers ici
                </p>
                <small>
                  Formats : {ALLOWED_EXTENSIONS.slice(0, 9).join(', ')}, images.
                  Taille max&nbsp;: {MAX_FILE_SIZE_MB} Mo / fichier.
                </small>
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  accept={ACCEPT_ATTR}
                  onChange={handleFilesSelected}
                  hidden
                />
              </div>
              {errors.files && <span className="form-err">{errors.files}</span>}

              {files.length > 0 && (
                <ul className="file-list">
                  {files.map((f, i) => (
                    <li key={`${f.name}-${i}`}>
                      <FaPaperclip />
                      <span className="file-name">{f.name}</span>
                      <span className="file-size">
                        {(f.size / 1024).toFixed(0)} Ko
                      </span>
                      <button
                        type="button"
                        className="file-remove"
                        onClick={() => removeFile(i)}
                        aria-label={`Retirer ${f.name}`}
                      >
                        <FaTimes />
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {status && (
              <div className={`form-status ${status.ok ? 'ok' : 'err'}`}>
                {status.ok ? <FaCheckCircle /> : <FaExclamationTriangle />}
                <span>{status.msg}</span>
              </div>
            )}

            <button
              type="submit"
              className="form-submit"
              disabled={submitting}
            >
              {submitting ? 'Envoi en cours...' : (
                <>
                  Envoyer ma demande <FaPaperPlane />
                </>
              )}
            </button>
          </form>
        </div>
      </section>

      <Footer />
    </>
  );
};

export default Contact;
